import {
  Component,
  OnInit,
  DestroyRef,
  inject,
  input,
  output,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';
import * as AlertingActions from '@core-app/state/alerting/alerting.actions';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectReceiversGroups } from '@core-app/state/alerting';
import { AlertingValidation } from '@validations/alerting.validation';

@Component({
  selector: 'fip-non-confirming-parts-leaving-supplier-form',
  templateUrl: './non-confirming-parts-leaving-supplier-form.component.html',
  styleUrl: './../../monitor-form.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NonConfirmingPartsLeavingSupplierFormComponent implements OnInit {
  /**
   * Injected dependencies
   */
  private readonly _store = inject(Store);
  private readonly _destroyRef = inject(DestroyRef);

  /**
   * Properties
   */
  readonly today = new Date();

  /**
   * Outputs
   */
  readonly formSubmit = output<FormGroup>();

  /**
   * Inputs
   */
  readonly submitTitle = input<string>('Create Monitor');
  readonly initialValues = input<{
    name?: string;
    initialQueryDate?: string;
    recipientsGroupId?: string;
  }>();

  /**
   * Observable streams from the store
   */
  recipientsGroups$ = this._store.select(selectReceiversGroups());

  /**
   * Reactive form group
   */
  formGroup = new FormGroup({
    name: new FormControl('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    initialQueryDate: new FormControl<Date>(this.today, {
      validators: [
        Validators.required,
        AlertingValidation.beforeTodayValidator(),
      ],
      nonNullable: true,
    }),
    recipientsGroupId: new FormControl(
      { value: '', disabled: true },
      { validators: Validators.required, nonNullable: true },
    ),
  });

  /**
   * Getters for the form controls
   */
  get nameControl() {
    return this.formGroup.get('name');
  }

  get initialQueryDateControl() {
    return this.formGroup.get('initialQueryDate');
  }

  get recipientsGroupIdControl() {
    return this.formGroup.get('recipientsGroupId');
  }

  constructor() {
    /**
     * Initialize form with input values when provided
     */
    effect(() => {
      const values = this.initialValues();

      if (!values) {
        return;
      }

      const { initialQueryDate, ...remainingValues } = values;

      if (initialQueryDate) {
        // Disable initial query date field and set required validator if value exists
        this.initialQueryDateControl?.setValidators([Validators.required]);
        this.initialQueryDateControl?.disable();
        this.initialQueryDateControl?.updateValueAndValidity();
      }

      const updates: Partial<{
        name: string;
        initialQueryDate: Date;
        recipientsGroupId: string;
      }> = {
        ...remainingValues,
        ...(initialQueryDate && {
          initialQueryDate: new Date(initialQueryDate),
        }),
      };

      if (Object.keys(updates).length > 0) {
        this.formGroup.patchValue(updates);
      }
    });
  }

  ngOnInit() {
    this._store.dispatch(AlertingActions.fetchReceiversGroup());
  }

  /**
   * Handles form submission when user creates a monitor.
   *
   * Validates the form and dispatches an action to upsert the monitor
   * with the configured settings.
   */
  submit(): void {
    if (this.formGroup.valid) {
      this.formSubmit.emit(this.formGroup);
    }
  }
}
